import { useQuery } from '@tanstack/react-query';
import Loader from '__components/Kizen/Loader';
import BasicModal from '__components/Modals/presets/BasicModal';
import { createSetupIntentQuery } from 'query/billing';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useMemo } from 'react';
import { SetupForm } from './SetupForm';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Typography } from '@kizen/kds/Typography';
import { useHistory } from 'react-router-dom';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { gray } from '@kizen/kds/Colors';

const STRIPE_RETRIES = 3;

const loadStripeWithRetry = async (iterations = 0): Promise<Stripe | null> => {
  const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  if (!STRIPE_PUBLISHABLE_KEY) {
    return null;
  }

  try {
    const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);

    return stripe;
  } catch (error) {
    if (iterations < STRIPE_RETRIES) {
      await new Promise((resolve) =>
        setTimeout(resolve, 400 * (iterations + 1))
      );

      return loadStripeWithRetry(iterations + 1);
    } else {
      monitoringExceptionHelper(
        `Failed to load Stripe.js after ${STRIPE_RETRIES} attempts`,
        { originalException: error }
      );

      return null;
    }
  }
};

export const AddCardModal = (props: any) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { data, isLoading, error } = useQuery(
    createSetupIntentQuery({
      gcTime: 0,
      enabled: props.show,
      throwOnError: false,
      retry: false,
    })
  );

  const isAddressError = (error as AxiosError)?.response?.status === 400;

  const stripeOptions = useMemo(() => {
    if (!data) {
      return;
    }

    return {
      appearance: {
        variables: {
          colorText: gray['gray-800'],
          colorTextSecondary: gray['gray-800'],
          fontSizeBase: '14px',
          fontFamily: '"Proxima Nova", sans-serif',
        },
      },
      clientSecret: data?.client_secret,
    };
  }, [data]);

  if (isAddressError) {
    return (
      <BasicModal
        {...props}
        size="small"
        heading={t('Business Address Required')}
        buttonText={t('Add Address')}
        onConfirm={() => {
          history.push('/settings/business');
        }}
      >
        <div className="pb-2">
          <Typography>
            {t(
              'In order to add a payment method, please add your business address first and try again.'
            )}
          </Typography>
        </div>
      </BasicModal>
    );
  }

  return (
    <BasicModal
      {...props}
      hideFooter
      size="large"
      heading={t('Add Payment Method')}
    >
      {isLoading || !stripeOptions ? (
        <div className="min-h-[188px] flex items-center justify-center">
          <Loader loading paddingTop="0" />
        </div>
      ) : (
        <Elements stripe={loadStripeWithRetry()} options={stripeOptions}>
          <SetupForm onCancel={props.onHide} existingIds={props.existingIds} />
        </Elements>
      )}
    </BasicModal>
  );
};
